import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

/**
 * React hook that fires a callback whenever ResizeObserver
 * detects a change to target element's size.
 */
const useResizeObserver = (
  target: MutableRefObject<HTMLElement> | HTMLElement,
  callback = () => {},
): void => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const targetEl = target && 'current' in target ? target.current : target;
  const updater = useCallback<ResizeObserverCallback>(entries => {
    entries.forEach(callback);
  }, [callback]);

  useEffect(() => {
    if (!targetEl) {
      return () => {};
    }
    if (!observerRef.current) {
      observerRef.current = new ResizeObserver(updater);
    }
    const { current: resizeObserver } = observerRef;
    resizeObserver.observe(targetEl);

    return () => {
      resizeObserver.unobserve(targetEl);
    };
  }, [callback, targetEl, updater]);
};

export default useResizeObserver;
